import './index.css';

// import { StrictMode } from 'react';
import i18n from 'i18next';
import HttpApi from 'i18next-http-backend';
import ReactDOM from 'react-dom/client';
import { initReactI18next } from 'react-i18next';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { registerSW } from 'virtual:pwa-register';
import * as Sentry from '@sentry/react';
import createCache, { EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

import App from './App.tsx';

declare global {
  interface Window {
    __MUI_NONCE__?: string;
  }
}

registerSW({ immediate: true });

// https://vite.dev/guide/build#load-error-handling
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  window.location.reload();
});

// https://mui.com/material-ui/guides/content-security-policy/
function createNonceChacheForMUI() : EmotionCache{
  const nonce = typeof window !== 'undefined' ? window.__MUI_NONCE__ : undefined;
  const nonceCache = createCache({
      key: 'css',
      nonce: nonce,
      prepend: true, 
    });
  return nonceCache;
} 

// https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/
if (USE_SENTRY) {
  Sentry.init({   
    dsn: import.meta.env.VITE_SENTRY_DNS,
    release: VERSION,
    environment: ENVIRONMENT,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['warn', 'error'] }),
      //using replays crashes the CSP style rules, so i turned it off
      //not yet solved: https://github.com/getsentry/sentry-javascript/issues/10481
    ],
    tracesSampleRate: 0.15, //down from 1, to prevent HTTP 429 errors
    sendDefaultPii: true,
    ignoreErrors: [
      'Invalid LatLng object: (NaN, NaN)',
      'Request aborted',
      'timeout of 60000ms exceeded',
      'Network Error',
      'Request failed with status code 401',
      'Request failed with status code 500',
      'CanceledError: canceled',
    ],
  });
}

// TODO meg a browser language-et is hasznalhatnank
// TODO miert 'hu' ha nincs mas es 'en' a default ?
const params = new URLSearchParams(document.location.search);
const l = params.get('l');
const language = l || localStorage.getItem('language') || 'hu';
const defaultLanguage = 'hu';
const buildNumber = BUILD_NUMBER;

i18n
  // FIXME .use(LanguageDetector)
  .use(HttpApi)
  .use(initReactI18next)
  .init({
    lng: language,
    load: 'languageOnly',
    backend: {
      loadPath: `/i18n/{{lng}}.json?v=${buildNumber}`, // must be relative so it works with base url
    },
    fallbackLng: defaultLanguage,
    interpolation: {
      escapeValue: true,
    },
  });

//For the api generation
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (_error, _query) => {
      console.debug(_error, _query);
    },
  }),
  mutationCache: new MutationCache({
    onError: (_error, _variables, _context, _mutation) => {
      console.debug(_error, _variables, _context, _mutation);
    },
  }),
});

const theme = createTheme({
  cssVariables: true,
  colorSchemes: { light: true, dark: false },
  typography: {
    allVariants: {
      fontFamily: "'Exo 2'",
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  // <StrictMode>
  <QueryClientProvider client={queryClient}>
    <CacheProvider value={createNonceChacheForMUI()}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        <App />
      </ThemeProvider>
    </CacheProvider>
  </QueryClientProvider>
  // </StrictMode>
);
